/**
 * StorySaved (2a) — shown after creating a story.
 * Route: /my-life/saved/:storyId
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Check, ArrowRight, Ear, BookOpen, ChevronRight } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { axiosInstance } from '../services/api';
import { getMediaServeUrl } from '../config/mediaAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

export default function StorySaved() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [story, setStory] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [showChapterPicker, setShowChapterPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [childList, setChildList] = useState(location.state?.children || []);

  useEffect(() => {
    if (!storyId) return;
    // Fetch story
    axiosInstance.get(`/lifeevent/events/${storyId}/`).then(async res => {
      setStory(res.data);

      // Which children is this story for? Prefer router state; fall back to the saved event.
      let kids = location.state?.children || [];
      if (kids.length === 0) {
        const ids = res.data?.event_data?.child_ids || [];
        if (ids.length > 0) {
          try {
            const grouped = await axiosInstance.get('/genealogy/people/my_family_grouped/');
            kids = (grouped.data.children || []).filter(c => ids.includes(c.id));
          } catch { /* names are optional */ }
        }
      }
      setChildList(kids);

      // Fetch the chapters of the person the story belongs to (child if single child, else self)
      const chapterUrl = kids.length === 1
        ? `/chapters/my_life_data/?person_id=${kids[0].id}`
        : '/chapters/my_life_data/';
      axiosInstance.get(chapterUrl).then(chapRes => {
        const chs = chapRes.data.chapters || [];
        setChapters(chs);
        // Find the chapter containing this story via memories
        const storyChapter = chs.find(c =>
          (c.memories_preview || []).some(m => m.life_event_id === parseInt(storyId))
        );
        setChapter(storyChapter || chs[chs.length - 1]);
      }).catch(() => {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [storyId]);

  const handleMoveChapter = async (newChapterId) => {
    try {
      // Find the memory linked to this story and update its chapter
      await axiosInstance.patch(`/chapter-memories/by-life-event/${storyId}/`, {
        chapter: newChapterId,
      }).catch(() => {
        // If no specific endpoint, try updating via the chapter
      });
      const newCh = chapters.find(c => c.id === newChapterId);
      if (newCh) setChapter(newCh);
      setShowChapterPicker(false);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <HubLayout activeView="life">
        <div style={{ maxWidth: 460, margin: '0 auto', padding: '60px 24px', textAlign: 'center', color: '#828A76' }}>
          Loading...
        </div>
      </HubLayout>
    );
  }

  const media = story?.media_files || [];
  const videoCount = media.filter(m => m.media_type === 'video').length;
  const photoCount = media.filter(m => m.media_type === 'image').length;
  const processingCount = media.filter(m => m.media_type === 'video').length; // assume all videos are processing initially
  const mediaItems = media.slice(0, 4).map(m => ({
    id: m.id,
    type: m.media_type,
    url: m.file_url || getMediaServeUrl(m.id),
    thumbUrl: m.id ? `/api/media/files/${m.id}/serve-thumbnail/` : null,
  }));

  const mediaMeta = [
    videoCount > 0 && `${videoCount} video${videoCount > 1 ? 's' : ''}`,
    photoCount > 0 && `${photoCount} photo${photoCount > 1 ? 's' : ''}`,
    'Today',
  ].filter(Boolean).join(' · ');

  const parentName = story?.user_name || 'You';
  const kidNames = childList.map(c => [c.first_name, c.last_name].filter(Boolean).join(' '));
  const childNames = kidNames.length > 2
    ? `${kidNames.slice(0, -1).join(', ')}, and ${kidNames[kidNames.length - 1]}`
    : kidNames.join(' and ');

  const yearRange = chapter ? `${chapter.start_year}–${chapter.end_year || 'Present'}` : '';

  return (
    <HubLayout activeView="life">
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '48px 24px 60px', textAlign: 'center' }}>

        {/* Check circle */}
        <div style={{
          width: 72, height: 72, borderRadius: '50%', background: '#3E4F22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <Check size={36} color="#fff" />
        </div>

        {/* Title */}
        <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 30, color: '#2C2F26', margin: '0 0 10px' }}>
          Your story is saved
        </h1>

        {/* Parent / child */}
        {childList.length > 0 && (
          <div style={{
            background: '#F1F5E8', border: '1.5px solid #D6D1BD', borderRadius: 12,
            padding: '12px 16px', margin: '0 0 16px', fontFamily: FONT_B, fontSize: 17,
            color: '#2C2F26', lineHeight: 1.6, textAlign: 'left',
          }}>
            <div><span style={{ color: '#4A4F40' }}>Recorded by:</span> <strong>{parentName}</strong></div>
            <div><span style={{ color: '#4A4F40' }}>Recorded for:</span> <strong>{childNames}</strong></div>
          </div>
        )}

        {/* Chapter info */}
        <p style={{ fontSize: 18, color: '#3F4436', fontFamily: FONT_B, margin: '0 0 28px', lineHeight: 1.5 }}>
          {childList.length > 1 ? (
            <>It's saved once and linked to <strong style={{ color: '#2C2F26' }}>{childNames}</strong>'s life stories.</>
          ) : (
            <>
              It's in the chapter <strong style={{ color: '#2C2F26' }}>{chapter?.title || (childList.length === 1 ? `${childList[0].first_name}'s life` : 'your life')}</strong>
              {yearRange && ` (${yearRange})`} of {childList.length === 1 ? `${childList[0].first_name}'s` : 'your'} life story.
            </>
          )}
        </p>

        {/* Story card */}
        <div style={{
          background: '#fff', border: '1.5px solid #D6D1BD', borderRadius: 16,
          overflow: 'hidden', textAlign: 'left', marginBottom: 24,
        }}>
          {/* Media tile */}
          {mediaItems.length > 0 && (
            <div style={{
              height: 200, overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: mediaItems.length >= 2 ? '1fr 1fr' : '1fr',
              gridTemplateRows: mediaItems.length >= 3 ? '1fr 1fr' : '1fr',
              gap: 2,
            }}>
              {mediaItems.map((item, i) => (
                <div key={i} style={{ overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                  {item.type === 'video' ? (
                    <video src={`${item.url}#t=0.1`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} muted preload="metadata" playsInline />
                  ) : (
                    <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: '16px 18px' }}>
            <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 19, color: '#2C2F26', margin: '0 0 4px' }}>
              {story?.title || 'Untitled'}
            </h3>
            <p style={{ fontSize: 15, color: '#4A4F40', fontFamily: FONT_B, margin: 0 }}>{mediaMeta}</p>
          </div>

          {/* Processing strip */}
          {videoCount > 0 && (
            <div style={{
              background: '#FBFAF3', borderTop: '1px solid #EFEDE2', padding: '12px 18px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <Ear size={18} color="#C65D00" />
              <span style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>
                We're listening to your {videoCount === 1 ? 'video' : `${videoCount} videos`}. When {videoCount === 1 ? "it's" : "they're"} ready, we'll ask you who's talking.
              </span>
            </div>
          )}
        </div>

        {/* Primary CTA */}
        <button onClick={() => navigate(`/my-life?new=${storyId}`)} style={{
          width: '100%', minHeight: 58, borderRadius: 13, border: 'none',
          background: '#3E4F22', color: '#fff', fontSize: 18, fontWeight: 700,
          cursor: 'pointer', fontFamily: FONT_B, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8, marginBottom: 20,
        }}>
          See it in your life story <ArrowRight size={20} />
        </button>

        {/* Secondary links */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
          {childList.length <= 1 && (
            <button onClick={() => setShowChapterPicker(true)} style={{
              background: 'none', border: 'none', color: '#3E4F22', fontSize: 16,
              fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
              textDecoration: 'underline', textUnderlineOffset: '3px', padding: '10px 4px',
            }}>Move to another chapter</button>
          )}
          <button onClick={() => navigate('/tell-a-story')} style={{
            background: 'none', border: 'none', color: '#3E4F22', fontSize: 16,
            fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
            textDecoration: 'underline', textUnderlineOffset: '3px', padding: '10px 4px',
          }}>Add another story</button>
        </div>

        {/* Chapter picker modal */}
        {showChapterPicker && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(28,32,22,.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => setShowChapterPicker(false)}>
            <div style={{
              background: '#fff', borderRadius: 18, maxWidth: 400, width: '90vw',
              padding: '24px', maxHeight: '70vh', overflowY: 'auto',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, margin: '0 0 16px' }}>
                Move to another chapter
              </h3>
              {chapters.map(ch => (
                <button key={ch.id} onClick={() => handleMoveChapter(ch.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                  padding: '14px 12px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: ch.id === chapter?.id ? '#EAF1DC' : '#fff',
                  textAlign: 'left', marginBottom: 4, minHeight: 56, fontFamily: FONT_B,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: '#3E4F22',
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>{ch.number}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26' }}>{ch.title}</div>
                    <div style={{ fontSize: 13, color: '#4A4F40' }}>{ch.start_year}–{ch.end_year || 'Present'}</div>
                  </div>
                  {ch.id === chapter?.id && <Check size={18} color="#3E4F22" style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </HubLayout>
  );
}
